import { useState } from "react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useParams, useNavigate, useLocation, NavLink, Outlet } from "react-router-dom";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@satyrsmc/shared/components";
import type { Contact } from "@satyrsmc/shared/client";
import type { MemberGetOutput } from "@satyrsmc/shared/dto/admin/member";
import type { UserGetOutput, Registration } from "@satyrsmc/shared/dto/admin/user";
import { contactsToVCardFileAsync } from "@/lib/vcard";
import {
  ArrowLeft,
  Pencil,
  Download,
  Trash2,
  RotateCcw,
  Mail,
  UserPlus,
  ChevronDown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CreateContactAccountDialog } from "@/components/users/InviteUserDialog";
import { EditContactDialog } from "./EditContactDialog";
import { trpc } from "@/trpc";
import {
  useContactSuspense,
  useDeleteContact,
  useRestoreContact,
  useInvalidateQueries,
  unwrapSuspenseData,
  useMemberByContactId,
  useUserByContactId,
} from "@/queries/hooks";
import { useRegistrationByContactId } from "@/queries/users";

export interface ContactDetailContext {
  contact: Contact;
  memberData: MemberGetOutput | null | undefined;
  userData: UserGetOutput | null | undefined;
  registrationData: Registration | null | undefined;
  refresh: () => void;
}

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) return null;
  return <ContactDetailContent id={id} />;
}

function ContactDetailContent({ id }: { id: string }) {
  const { confirm, confirmDialog } = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const invalidate = useInvalidateQueries();
  const contact = unwrapSuspenseData(useContactSuspense(id))!;
  const deleteContactMutation = useDeleteContact();
  const restoreContactMutation = useRestoreContact();
  const { data: memberData } = useMemberByContactId(id);
  const { data: userData } = useUserByContactId(id);
  const { data: registrationData } = useRegistrationByContactId(id);
  const utils = trpc.useUtils();
  const promoteMutation = trpc.admin.members.promoteContact.useMutation({
    onSuccess: () => {
      utils.admin.members.getByContactId.invalidate({ contact_id: id });
    },
  });
  const [editOpen, setEditOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const refresh = () => {
    invalidate.invalidateContact(id);
  };

  const handleExportVCard = async () => {
    const vcf = await contactsToVCardFileAsync([contact]);
    const blob = new Blob([vcf], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${contact.display_name.replace(/[^a-z0-9]/gi, "_")}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async () => {
    if (!(await confirm("Soft delete this contact? You can restore it later."))) return;
    await deleteContactMutation.mutateAsync(id);
    navigate((location.state as { from?: string })?.from ?? "/admin/contacts");
  };

  const handleRestore = async () => {
    await restoreContactMutation.mutateAsync(id);
    refresh();
  };

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
      isActive
        ? "border-primary text-foreground"
        : "border-transparent text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Button
          variant="ghost"
          onClick={() => navigate((location.state as { from?: string })?.from ?? "/admin/contacts")}
        >
          <ArrowLeft className="size-4" />
          Back to Contacts
        </Button>
        <div className="flex gap-2">
          {contact.status === "deleted" ? (
            <Button variant="outline" onClick={handleRestore}>
              <RotateCcw className="size-4" />
              Restore
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={promoteMutation.isPending}>
                  {promoteMutation.isPending ? "Promoting..." : "Actions"}
                  <ChevronDown className="size-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setEditOpen(true)}>
                  <Pencil className="size-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportVCard}>
                  <Download className="size-4" />
                  Export vCard
                </DropdownMenuItem>
                {memberData == null && (
                  <DropdownMenuItem
                    onClick={() => promoteMutation.mutate({ contact_id: id })}
                    disabled={promoteMutation.isPending}
                  >
                    <UserPlus className="size-4" />
                    Promote to Member
                  </DropdownMenuItem>
                )}
                {userData == null && registrationData == null && (
                  <DropdownMenuItem onClick={() => setInviteOpen(true)}>
                    <Mail className="size-4" />
                    Create account
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={handleDelete}
                >
                  <Trash2 className="size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{contact.display_name}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1 flex flex-wrap items-center gap-2">
            <span>{contact.type}</span>
            <span>•</span>
            {contact.status === "active" ? (
              <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-400">
                Active
              </span>
            ) : (
              <span>{contact.status}</span>
            )}
            {contact.hellenic && (
              <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                Hellenic
              </span>
            )}
            {contact.deceased && (
              <span className="inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                Deceased{contact.deceased_year ? ` (${contact.deceased_year})` : ""}
              </span>
            )}
            {contact.organization_name && <span>• {contact.organization_name}</span>}
          </p>
          <nav className="flex gap-1 border-b border-border mt-4 -mb-4">
            <NavLink to={`/admin/contacts/${id}`} end className={tabClass}>
              Contact Info
            </NavLink>
            {memberData != null && (
              <NavLink to={`/admin/contacts/${id}/member`} className={tabClass}>
                Member Details
              </NavLink>
            )}
            {(userData != null || registrationData != null) && (
              <NavLink to={`/admin/contacts/${id}/user`} className={tabClass}>
                User Account
              </NavLink>
            )}
          </nav>
        </CardHeader>

        <Outlet
          context={
            {
              contact,
              memberData,
              userData,
              registrationData,
              refresh,
            } satisfies ContactDetailContext
          }
        />
      </Card>

      <EditContactDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        contact={contact}
        onSuccess={refresh}
      />

      <CreateContactAccountDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        contactId={contact.id}
        displayName={contact.display_name}
        onSuccess={refresh}
      />

      {confirmDialog}
    </div>
  );
}
